// White label: динамічний брендинг клієнтського порталу за Host.
//
// На партнерських доменах (tenant_domain у БД) бекенд віддає публічний
// GET /branding: назву бренду, кольори (повна палітра, виведена з одного
// primary), логотип/favicon/manifest. Тут ми його застосовуємо і кешуємо
// в localStorage['wl:'+hostname] — синхронний inline-скрипт в index.html
// читає цей кеш ДО першого рендера, щоб не блимав дефолтний cloudGPS
// (той самий прийом, що theme-скрипт).
//
// На core-хостах (globus./client.cloudgps.online, dev) — повний no-op:
// існуючий статичний мультибрендинг utils/branding.js працює як раніше.
import { ref } from 'vue'

const API_BASE = import.meta.env.VITE_CLIENT_API_BASE || '/client/api'

const CORE_HOSTS = ['globus.cloudgps.online', 'client.cloudgps.online', 'localhost', '127.0.0.1']
const HOST = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase()
const CACHE_KEY = 'wl:' + HOST

// Реактивний стан для App.vue (логотип у шапці до логіна). Не-Vue споживачі
// (i18n, branding.js) читають через getWlBrand() — їм реактивність не потрібна.
export const wlBrandRef = ref(null)

export const isWhiteLabel = () => wlBrandRef.value !== null
export const getWlBrand = () => wlBrandRef.value

const isCoreHost = () => HOST === '' || CORE_HOSTS.includes(HOST)

// Гідратація з кешу при імпорті модуля: повторний візит отримує бренд
// (логотип у шапці, ім'я в i18n) одразу, не чекаючи відповіді API.
try {
  if (!isCoreHost()) {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    if (cached && cached.n) {
      wlBrandRef.value = {
        id: 'wl',
        name: cached.n,
        shortName: cached.n,
        logo: cached.logo || null,
        loginBg: cached.bg || null,
      }
    }
  }
} catch { /* битий кеш — приберемо при наступному fetch */ }

// CSS-текст із серверної палітри: :root + темна тема одним тегом <style>,
// який чисто перекриває style.css в обох темах.
function buildCssText(colors) {
  let css = ':root{'
    + '--primary:' + colors.primary + ';'
    + '--primary-hover:' + colors.primaryHover + ';'
    + '--primary-dark:' + colors.primaryDark + ';'
    + '--primary-light:' + colors.primaryLight + ';'
    + '--primary-bg:' + colors.primaryBg + ';'
    + '--primary-text:' + colors.primaryText + '}'
  if (colors.dark) {
    css += 'html[data-theme="dark"]{'
      + '--primary-light:' + colors.dark.primaryLight + ';'
      + '--primary-bg:' + colors.dark.primaryBg + ';'
      + '--primary-text:' + colors.dark.primaryText + '}'
  }
  return css
}

// Застосовуємо бренд до DOM (дзеркалить inline-скрипт в index.html —
// той працює з кешу до рендера, цей — після свіжої відповіді API)
function applyBrand(cache) {
  document.title = cache.n
  const appleTitle = document.getElementById('apple-app-title')
  if (appleTitle) appleTitle.setAttribute('content', cache.n)

  const swap = (id, href) => {
    const el = document.getElementById(id)
    if (el && href) el.setAttribute('href', href)
  }
  swap('favicon-32', cache.f32)
  swap('favicon-64', cache.f64)
  swap('apple-touch-icon-link', cache.f180)
  swap('pwa-manifest', cache.man)

  if (cache.tc) {
    const tcEl = document.querySelector('meta[name="theme-color"]')
    if (tcEl) tcEl.setAttribute('content', cache.tc)
  }

  const prev = document.getElementById('wl-brand-css')
  if (prev) prev.remove()
  if (cache.css) {
    const st = document.createElement('style')
    st.id = 'wl-brand-css'
    st.textContent = cache.css
    document.head.appendChild(st)
  }
}

function clearBrand() {
  try { localStorage.removeItem(CACHE_KEY) } catch { /* ignore */ }
  wlBrandRef.value = null
  const prev = document.getElementById('wl-brand-css')
  if (prev) prev.remove()
}

// Викликається з main.js після mount (не блокує старт застосунку).
export async function initWhiteLabel() {
  if (isCoreHost()) return

  let data
  try {
    // Свій fetch, а не apiRequest: ендпоінт публічний, авторизація/redirect
    // на 401 тут не потрібні й лише заважали б на логін-сторінці
    const res = await fetch(API_BASE + '/branding', { headers: { Accept: 'application/json' } })
    if (res.status === 404) {
      // Домен більше не активний (вимкнули опцію/видалили) — чистимо кеш
      clearBrand()
      return
    }
    if (!res.ok) return // мережа/5xx: залишаємо кеш як є
    data = await res.json()
  } catch {
    return // офлайн — працюємо з кешованим брендом
  }

  if (!data || data.whiteLabel !== true) {
    clearBrand()
    return
  }

  // URL ассетів будуємо самі від API_BASE (сервер віддає прод-шляхи /api/...,
  // які в dev за проксі /client/api не резолвляться)
  const asset = (p) => API_BASE + '/branding/' + p
  const v = data.v || ''
  const hasLogo = !!data.logo
  const cache = {
    v,
    n: data.brandName || '',
    css: data.colors ? buildCssText(data.colors) : '',
    tc: data.colors ? data.colors.primary : null,
    logo: hasLogo ? asset('logo?v=' + v) : null,
    f32: hasLogo ? asset('favicon?size=32&v=' + v) : null,
    f64: hasLogo ? asset('favicon?size=64&v=' + v) : null,
    f180: hasLogo ? asset('favicon?size=180&v=' + v) : null,
    man: asset('manifest?v=' + v),
    // Фон страницы логина (может отсутствовать — тогда стандартная сетка в цвете бренда)
    bg: data.loginBg ? asset('login-bg?v=' + v) : null,
  }

  wlBrandRef.value = { id: 'wl', name: cache.n, shortName: cache.n, logo: cache.logo, loginBg: cache.bg }
  applyBrand(cache)

  try {
    const serialized = JSON.stringify(cache)
    if (localStorage.getItem(CACHE_KEY) !== serialized) {
      localStorage.setItem(CACHE_KEY, serialized)
    }
  } catch { /* приватний режим — переживемо без кешу */ }

  // Сигнал споживачам (i18n перезбирає словник із новим ім'ям бренду)
  window.dispatchEvent(new CustomEvent('wl-brand-ready'))
}
